import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchComCatBrandCatalog, type ComCatBrandItem } from '@/api/comCatBrand';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import PrevBrandPicker, { isPrevBrandSelectionValid } from '@/components/survey/PrevBrandPicker';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import {
  Q7_2_OPTIONS_DICE,
  buildQ7_2Answers,
  buildQ7_2DiceAnswers,
  readQ7_2Answers,
  writeQ7_2Answers,
  type Q7_2OptionDice,
} from '@/lib/q7_2Storage';
import {
  PREV_BRAND_OTHERS,
  buildOtherBrandsAnswers,
  isOtherBrandsComplete,
} from '@/lib/otherBrandsStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import type { AppTranslations } from '@/locales';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';

/** Match /7_1: bold white, body-size (not panel 20px). */
const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-base font-bold leading-snug text-white';

const checkboxClass =
  'mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-white/50 bg-black/20 accent-[#c9a882] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80';

/** Display label for a canonical (EN) Dice /7_2 option. */
function q7_2DiceOptionLabel(option: Q7_2OptionDice, t: AppTranslations['q7_2']): string {
  switch (option) {
    case 'Taste/flavour':
      return t.optionTasteFlavour;
    case 'Overall experience':
      return t.optionOverallExperience;
    case 'Appearance/pack':
      return t.optionAppearancePack;
    case 'Something different from my usual choice':
      return t.optionSomethingDifferent;
    case 'Nothing in particular':
      return t.optionNothingInParticular;
    default:
      return option;
  }
}

/**
 * LBS: usual smoke brand/variant picker → `/7_3`.
 * Dice: like-most single select → `/7_3`.
 */
export default function Q7_2() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const stored = readQ7_2Answers();
  const [brands, setBrands] = useState<ComCatBrandItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState(() => stored?.brand ?? '');
  const [selectedVariant, setSelectedVariant] = useState(() => stored?.variant ?? '');
  const [diceSelected, setDiceSelected] = useState<Q7_2OptionDice | null>(() => {
    const s = stored?.selected[0];
    return s && (Q7_2_OPTIONS_DICE as readonly string[]).includes(s) ? (s as Q7_2OptionDice) : null;
  });
  const navigateAfterSelectRef = useRef(false);

  const isDice = codeBrand === 'Dice';

  useEffect(() => {
    if (isDice) return;
    writeQ7_2Answers(buildQ7_2Answers(selectedBrand, selectedVariant));
  }, [selectedBrand, selectedVariant, isDice]);

  useEffect(() => {
    if (isDice) return;
    if (!navigateAfterSelectRef.current) return;
    if (!isPrevBrandSelectionValid(selectedBrand, selectedVariant)) return;
    navigateAfterSelectRef.current = false;
    writeQ7_2Answers(buildQ7_2Answers(selectedBrand, selectedVariant));
    navigate('/7_3');
  }, [selectedBrand, selectedVariant, navigate, isDice]);

  useEffect(() => {
    if (!isDice) return;
    if (!navigateAfterSelectRef.current) return;
    navigateAfterSelectRef.current = false;
    navigate('/7_3');
  }, [diceSelected, navigate, isDice]);

  useEffect(() => {
    if (isDice) return;
    let cancelled = false;
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchComCatBrandCatalog();
        if (!cancelled) setBrands(list);
      } catch {
        if (!cancelled) setWarning(t.q7_2.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.q7_2.loadError, isDice]);

  useEffect(() => {
    if (isDice) return;
    if (selectedBrand && selectedBrand !== PREV_BRAND_OTHERS && !expandedBrand) {
      setExpandedBrand(selectedBrand);
    }
  }, [selectedBrand, expandedBrand, isDice]);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isOtherBrandsComplete()) {
    return <Navigate to="/otherbrands" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }

  const selectDiceOption = (label: Q7_2OptionDice) => {
    setDiceSelected((prev) => {
      if (prev === label) return prev;
      writeQ7_2Answers(buildQ7_2DiceAnswers(label));
      navigateAfterSelectRef.current = true;
      return label;
    });
  };

  const handleSelectVariant = (brand: string, variant: string) => {
    navigateAfterSelectRef.current = true;
    setSelectedBrand(brand);
    setSelectedVariant(variant);
    setWarning(null);
  };

  const handleOtherSpecifyChange = (value: string) => {
    const normalized = buildOtherBrandsAnswers(PREV_BRAND_OTHERS, value);
    setSelectedBrand(normalized.brand);
    setSelectedVariant(normalized.variant);
    setExpandedBrand(null);
    setWarning(null);
  };

  const handleOtherSpecifyCommit = () => {
    if (selectedBrand !== PREV_BRAND_OTHERS) return;
    const text = selectedVariant.trim();
    if (!text) {
      setWarning(t.q7_2.otherSpecifyWarning);
      return;
    }
    writeQ7_2Answers(buildQ7_2Answers(PREV_BRAND_OTHERS, text));
    navigate('/7_3');
  };

  if (isDice) {
    return (
      <SurveyBackgroundLayout>
        <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
          <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
            <SurveyBrownPanelInset className="flex min-h-0 flex-1 flex-col">
              <SurveyBrownPanel className="!flex !h-full !min-h-0 !flex-col !overflow-hidden !px-4 !pt-4 !pb-4">
                <p className="m-0 shrink-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                  {t.q7_2.diceQuestion}
                </p>
                <div className="mt-[4.5rem] min-h-0 flex-1 overflow-y-auto sm:mt-[5.25rem]">
                  <div className="flex flex-col gap-[1.875rem] pb-2 sm:gap-[2.25rem]">
                    {Q7_2_OPTIONS_DICE.map((option) => {
                      const id = `q72-dice-${option.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-']/g, '')}`;
                      const checked = diceSelected === option;
                      return (
                        <label key={option} htmlFor={id} className={checkboxLabelClass}>
                          <input
                            id={id}
                            type="checkbox"
                            checked={checked}
                            onChange={() => selectDiceOption(option)}
                            className={checkboxClass}
                          />
                          <span>{q7_2DiceOptionLabel(option, t.q7_2)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </SurveyBrownPanel>
            </SurveyBrownPanelInset>
          </div>
        </div>
      </SurveyBackgroundLayout>
    );
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
        {/* pt not mt: margin on flex-1 child is clipped by overflow-hidden (cuts bottom radius) */}
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
          {/* Panel fills viewport; list scrolls inside so bottom radius stays visible */}
          <SurveyBrownPanelInset className="flex min-h-0 flex-1 flex-col">
            <SurveyBrownPanel className="!flex !h-full !min-h-0 !flex-col !overflow-hidden !px-4 !pt-4 !pb-4">
              <div className="shrink-0">
                <p className="m-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                  {t.q7_2.question}
                </p>
                <p className="mt-16 m-0 text-left text-base font-bold text-white sm:mt-20">
                  {t.q7_2.yourAnswer}
                </p>
              </div>
              {/* text-base: override SurveyBrownPanel 20px so brands match /otherbrands */}
              <div className="mt-4 min-h-0 flex-1 overflow-y-auto text-base">
                <PrevBrandPicker
                  brands={brands}
                  loading={loading}
                  expandedBrand={expandedBrand}
                  selectedBrand={selectedBrand}
                  selectedVariant={selectedVariant}
                  onExpandedBrandChange={setExpandedBrand}
                  onSelectVariant={handleSelectVariant}
                  onOtherSpecifyChange={handleOtherSpecifyChange}
                  onOtherSpecifyCommit={handleOtherSpecifyCommit}
                />
                {warning != null ? (
                  <div className="mt-4">
                    <SurveyValidationAlert>{warning}</SurveyValidationAlert>
                  </div>
                ) : null}
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
